<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <div v-show="!isLoading">
    <div>
      <h1 class="font-semibold text-xl text-grey-light-900">Customize</h1>
      <p class="text-grey-light-600 text-sm">Set up your branding on your website.</p>
    </div>
    <SectionDivider class="my-4" />
    <input type="file" ref="fileInput" class="hidden" accept="image/png, jpg, image/jpeg" @change="onFileChosen" />
    <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
      <div class="col-span-2">
        <h5 class="font-semibold text-grey-light-900">Email Notifications</h5>
        <p class="text-grey-light-600 text-sm">Max image size is 750x240 pixels.</p>
      </div>
      <div class="col-span-2">
        <div v-if="formMerchant.features.enableCustomEmailLogo && !isLoadingEmail" class="flex flex-col gap-4">
          <div class="flex gap-2">
            <CheckboxComponent class="mt-1" v-model="formMerchant.customFeatures.logo.enabled" />
            <div class="flex flex-col gap-2">
              <span class="font-medium">Display Logo in Email Notifications</span>
              <span class="text-grey-light-600 text-sm">
                This logo will be used as the default logo in email correspondence sent to your customers.
              </span>
            </div>
          </div>

          <div v-if="formMerchant.customFeatures.logo.enabled">
            <div class="flex flex-col gap-4">
              <div v-if="formMerchant.customFeatures.logo.value" class="w-[250px]">
                <img
                  :src="`https://res.cloudinary.com/daucjyatt/image/upload/${formMerchant.customFeatures.logo.value}`"
                  class="rounded-lg hover:cursor-pointer"
                  alt="Custom email header logo"
                  @click="triggerFileInput('email')"
                />
              </div>
              <div class="flex gap-2">
                <Button
                  variant="primaryOutline"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="triggerFileInput('email')"
                >
                  Upload image
                </Button>
                <Button
                  v-if="formMerchant.customFeatures.logo.value"
                  variant="outline"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="onReviewEmail('email')"
                >
                  Review email
                </Button>
                <Button
                  v-if="formMerchant.customFeatures.logo.value"
                  variant="destructive"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="onRemoveImage('email')"
                >
                  Remove image
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 flex justify-start" v-else-if="isLoadingEmail">
          <LayupSpinner />
        </div>
        <div v-else class="mt-4 flex justify-start">
          <p>This feature is not enabled</p>
        </div>
      </div>
    </div>
    <SectionDivider class="my-4" />
    <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
      <div class="col-span-2">
        <h5 class="font-semibold text-grey-light-900">Default Product Image</h5>
        <p class="text-grey-light-600 text-sm">Max image size is 500x500 pixels.</p>
      </div>
      <div class="col-span-2">
        <div
          v-if="formMerchant.features.enableDefaultPaymentPlanImage && !isLoadingProduct"
          class="flex flex-col gap-4"
        >
          <div class="flex gap-2">
            <CheckboxComponent class="mt-1" v-model="formMerchant.customFeatures.imageUrl.enabled" />
            <div class="flex flex-col gap-2">
              <span class="font-medium">Default Product Image</span>
              <span class="text-grey-light-600 text-sm">
                This image will be used as the default if no image is used when payment plans are generated either
                through this dashboard or your e-commerce integration.
              </span>
            </div>
          </div>
          <div v-if="formMerchant.customFeatures.imageUrl.enabled">
            <div class="flex flex-col gap-4">
              <div v-if="formMerchant.customFeatures.imageUrl.value" class="w-[250px]">
                <img
                  :src="formMerchant.customFeatures.imageUrl.value"
                  class="rounded-lg hover:cursor-pointer"
                  alt="Default product image"
                  @click="triggerFileInput('product')"
                />
              </div>
              <div class="flex gap-2">
                <Button
                  variant="primaryOutline"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="triggerFileInput('product')"
                >
                  Upload image
                </Button>
                <Button
                  v-if="formMerchant.customFeatures.imageUrl.value"
                  variant="destructive"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="onRemoveImage('product')"
                >
                  Remove image
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 flex justify-start" v-else-if="isLoadingProduct">
          <LayupSpinner />
        </div>
        <div v-else class="mt-4 flex justify-start">
          <p>This feature is not enabled</p>
        </div>
      </div>
    </div>
    <SectionDivider class="my-4" />
    <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
      <div class="col-span-2">
        <h5 class="font-semibold text-grey-light-900">Payment Plan Widget</h5>
        <p class="text-grey-light-600 text-sm">Max image size is 750x240 pixels.</p>
      </div>

      <div class="col-span-2">
        <div v-if="formMerchant.features.enableCustomWidgetLogo && !isLoadingWidget" class="flex flex-col gap-4">
          <div class="flex gap-2">
            <CheckboxComponent class="mt-1" v-model="formMerchant.customFeatures.paymentWidget.enabled" />
            <div class="flex flex-col gap-2">
              <span class="font-medium">Display Logo on Payment Plan Widget</span>
              <span class="text-grey-light-600 text-sm">
                This logo will appear in the header of the payment widget used by your customers to initiate payment
                plans.
              </span>
            </div>
          </div>
          <div v-if="formMerchant.customFeatures.paymentWidget.enabled">
            <div class="flex flex-col gap-4">
              <div v-if="formMerchant.customFeatures.paymentWidget.value" class="w-[250px]">
                <img
                  :src="formMerchant.customFeatures.paymentWidget.value"
                  class="rounded-lg hover:cursor-pointer"
                  alt="Default product image"
                  @click="triggerFileInput('widget')"
                />
              </div>
              <div class="flex gap-2">
                <Button
                  variant="primaryOutline"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="triggerFileInput('widget')"
                >
                  Upload image
                </Button>
                <Button
                  v-if="formMerchant.customFeatures.paymentWidget.value"
                  variant="destructive"
                  type="button"
                  size="sm"
                  class="w-auto"
                  @click="onRemoveImage('widget')"
                >
                  Remove image
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 flex justify-start" v-else-if="isLoadingWidget">
          <LayupSpinner />
        </div>
        <div v-else class="mt-4 flex justify-start">
          <p>This feature is not enabled</p>
        </div>
      </div>
    </div>
    <SectionDivider class="my-4" />
    <div
      class="flex justify-end"
      v-if="
        formMerchant.features.enableCustomWidgetLogo ||
        formMerchant.features.enableCustomEmailLogo ||
        formMerchant.features.enableDefaultPaymentPlanImage
      "
    >
      <Button type="button" class="w-auto" :disabled="isSaving" @click="onSaveMerchant">Save</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { CLOUDINARY_NAME, CLOUDINARY_UPLOAD_PRESET } from '@/config';
import { NetworkError } from '@/errors';
import { sendReviewEmail } from '@/services/emails';
import { saveMerchant } from '@/services/merchants';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import type { ModelCustomize, ModelFeature, ModelMerchantResponse, ModelNotification } from '@layup/api-sdk';

const props = defineProps<{
  settings?: {
    customFeatures: ModelCustomize;
    imageUrl: string;
    features: ModelFeature;
    successfulPayment: ModelNotification;
  };
  merchantId: string;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const isSaving = ref(false);
const isLoading = ref(false);
const isLoadingEmail = ref(false);
const isLoadingProduct = ref(false);
const isLoadingWidget = ref(false);
const selectedSection = ref<'email' | 'product' | 'widget'>();
const formMerchant = ref({
  customFeatures: {
    logo: { enabled: false, value: '' },
    imageUrl: { enabled: false, value: '' },
    paymentWidget: { enabled: false, value: '' },
  },
  imageUrl: '',
  features: {
    enableCustomWidgetLogo: false,
    enableCustomEmailLogo: false,
    enableDefaultPaymentPlanImage: false,
  },
});

const emit = defineEmits(['save:merchant']);
const toast = useToast();

watch(
  () => props.settings,
  (newSettings) => {
    if (!newSettings) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    formMerchant.value = {
      customFeatures: {
        logo: {
          enabled: newSettings?.customFeatures.logo?.enabled || false,
          value: newSettings?.customFeatures.logo?.value || '',
        },
        imageUrl: {
          enabled: newSettings?.customFeatures.imageUrl?.enabled || false,
          value: newSettings?.customFeatures.imageUrl?.value || '',
        },
        paymentWidget: {
          enabled: newSettings?.customFeatures.paymentWidget?.enabled || false,
          value: newSettings?.customFeatures.paymentWidget?.value || '',
        },
      },
      imageUrl: newSettings?.imageUrl || '',
      features: {
        enableCustomWidgetLogo: newSettings?.features?.enableCustomWidgetLogo || false,
        enableCustomEmailLogo: newSettings?.features?.enableCustomEmailLogo || false,
        enableDefaultPaymentPlanImage: newSettings?.features?.enableDefaultPaymentPlanImage || false,
      },
    };

    isLoading.value = false;
  },
  { immediate: true, deep: true },
);

const triggerFileInput = (section: 'email' | 'product' | 'widget') => {
  selectedSection.value = section;
  fileInput.value?.click();
};

const onFileChosen = (event: Event) => {
  if (selectedSection.value === 'email') isLoadingEmail.value = true;
  if (selectedSection.value === 'product') isLoadingProduct.value = true;
  if (selectedSection.value === 'widget') isLoadingWidget.value = true;

  const target = event.target as HTMLInputElement;
  const files = target.files;

  if (!files || files.length === 0) return;

  const file = files[0];

  // Check that file is less than 2mb
  if (file.size > 2 * 1024 * 1024) {
    toast.error('File is too large. Max size is 2MB.', { position: 'top' });
    return;
  }

  // Make sure image passes size constraints and upload
  const img = new Image();
  img.src = URL.createObjectURL(file);
  img.onload = () => {
    if (selectedSection.value === 'email' || selectedSection.value === 'widget') {
      if (img.width > 750 || img.height > 240) {
        toast.error('Image dimensions exceed the maximum of 750x240.', { position: 'top' });
        return;
      }
    } else {
      if (img.width > 500 || img.height > 500) {
        toast.error('Image dimensions exceed the maximum of 500x500.', { position: 'top' });
        return;
      }
    }

    uploadFile(file);
  };
  img.onerror = () => {
    toast.error('Invalid image file.', { position: 'top' });
  };
};

const uploadFile = async (file: File) => {
  const cloudName = CLOUDINARY_NAME;
  const uploadPreset = CLOUDINARY_UPLOAD_PRESET;
  const apiEndpoint = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;

  const formData = new FormData();
  formData.append('upload_preset', uploadPreset);
  formData.append('file', file);

  try {
    const response = await axios.post(apiEndpoint, formData, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      withCredentials: false,
    });

    addMetric('settings-customize-email', { status: 'success', channel: 'merchant', method: 'uploadFile' });

    const version = response.data.version;
    const secureUrl = response.data.secure_url;
    const publicId = response.data.public_id;
    const format = response.data.format;

    if (selectedSection.value === 'email') {
      formMerchant.value.customFeatures.logo.value = `v${version}/${publicId}.${format}`;
    }
    if (selectedSection.value === 'product') {
      formMerchant.value.customFeatures.imageUrl.value = secureUrl;
    }
    if (selectedSection.value === 'widget') {
      formMerchant.value.customFeatures.paymentWidget.value = secureUrl;
    }
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-customize-email', {
        status: 'network_error',
        channel: 'merchant',
        method: 'uploadFile',
      });
    } else {
      createException(error);
      addMetric('settings-customize-email', { status: 'error', channel: 'merchant', method: 'uploadFile' });
    }
    toast.error('An error occurred while saving merchant data. Please try again.');
  } finally {
    isLoadingEmail.value = false;
    isLoadingProduct.value = false;
    isLoadingWidget.value = false;
  }
};

const onRemoveImage = async (section: 'email' | 'product' | 'widget') => {
  if (section === 'email') {
    isLoadingEmail.value = true;
    formMerchant.value.customFeatures.logo.value = '';
  }
  if (section === 'product') {
    isLoadingProduct.value = true;
    formMerchant.value.customFeatures.imageUrl.value = '';
  }
  if (section === 'widget') {
    isLoadingWidget.value = true;
    formMerchant.value.customFeatures.paymentWidget.value = '';
  }

  isLoadingEmail.value = false;
  isLoadingProduct.value = false;
  isLoadingWidget.value = false;
};

const onReviewEmail = async (section: 'email' | 'product' | 'widget') => {
  let logo = '';

  if (section === 'email') {
    isLoadingEmail.value = true;
    logo = formMerchant.value.customFeatures.logo.value;
  }
  if (section === 'product') isLoadingProduct.value = true;
  if (section === 'email') isLoadingWidget.value = true;

  try {
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);
    const body = {
      emails: props.settings?.successfulPayment.emails ? props.settings?.successfulPayment.emails : [user.value.email],
      data: {
        merchantLogo: logo,
        clientName: user.value.name,
        orderName: 'Test Order',
        orderRef: 'Test Reference',
        orderId: '1',
        amount: '89',
        outstandingAmount: '09',
        type: 'review',
      },
    };
    await sendReviewEmail(body);
    addMetric('settings-customize-email', { status: 'success', channel: 'merchant', method: 'sendEmail' });
    toast.success('Review email sent successfully', { position: 'top' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-customize-email', {
        status: 'network_error',
        channel: 'merchant',
        method: 'sendEmail',
      });
    } else {
      createException(error);
      addMetric('settings-customize-email', { status: 'error', channel: 'merchant', method: 'sendEmail' });
    }
  } finally {
    isLoadingEmail.value = false;
    isLoadingProduct.value = false;
    isLoadingWidget.value = false;
  }
};

const onSaveMerchant = async () => {
  try {
    isLoading.value = true;
    if (props.merchantId === '') {
      toast.error('Merchant ID is not set. Please try reloading the page', { position: 'top' });
      throw new Error('Merchant ID is required.');
    }

    const settings: ModelMerchantResponse = { ...formMerchant.value };
    const response = await saveMerchant({ id: props.merchantId, modelMerchantRequest: settings });

    emit('save:merchant', response);

    addMetric('settings-customize-email', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settings-customize-email', {
        status: 'network_error',
        channel: 'merchant',
        method: 'saveMerchant',
      });
    } else {
      createException(error);
      addMetric('settings-customize-email', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
    }

    toast.error('Failed to send test email. Please try again', { position: 'top' });
  } finally {
    isSaving.value = false;
    isLoadingEmail.value = false;
    isLoadingProduct.value = false;
    isLoadingWidget.value = false;
    selectedSection.value = undefined;
  }
};
</script>
